import hasCookieCategoryConsent from '%paysera_service_agreement/js/CookiesUserAgreement/service/hasCookieCategoryConsent';
import {
    CATEGORY_MARKETING,
    CATEGORY_STATISTICS,
} from '%paysera_service_agreement/js/CookiesUserAgreement/config/constants';
import { subscribeToCookieConsentChange } from '%paysera_service_agreement/js/CookiesUserAgreement/service/cookieConsentChangeNotifier';
import isNonEmptyString from './isNonEmptyString';
import readAdvertisingConfig from './readAdvertisingConfig';
import {
    placeFacebookPixel,
    trackFacebookPixelEvent,
    placeGoogleAnalytics,
    sendGoogleAnalyticsPageView,
    placeGoogleTagManager,
    placeGoogleAdwords,
    trackGoogleAdwordsConversion,
    getServerPlacedTagKeys,
    getServerPlacedEventKeys,
    getServerDeclaredEventKeySpellings,
} from './organizerTagLoader';
import {
    VENDOR_FACEBOOK_PIXEL,
    VENDOR_GOOGLE_ADWORDS,
    VENDOR_GOOGLE_ANALYTICS,
    VENDOR_GOOGLE_TAG_MANAGER,
    readOrganizerTagVendor,
} from './organizerTagKeys';
import {
    CHECKOUT_FUNNEL_TRACKING_STEPS,
    FACEBOOK_PIXEL_EVENT_BY_STEP,
    FACEBOOK_PIXEL_PAGE_VIEW_EVENT,
    FACEBOOK_PIXEL_PURCHASE_EVENT,
    GOOGLE_ADWORDS_LABEL_KEY_BY_STEP,
    ORGANIZER_TRACKING_STEPS,
    buildOrganizerTrackingContextKeyPrefix,
} from './organizerTrackingSteps';
import withSessionStorage from './withSessionStorage';

const RELOAD_MARKER = 'redesign.organizerTracking.reload';
const RELOAD_REASON_RESIDENCY = 'residency';
const RELOAD_REASON_CONSENT = 'consent';

const reportedVendorKeys = new Set();
const consentedCategoriesInUse = new Set();
const categoriesTheClientIsFeeding = new Set();
const placedEventKeys = new Set();

let currentContext = null;
let consentSubscription = null;
let trackingSuspended = false;
let tagsCameFromTheMarkup = false;

function readTagIdentifier(config, key) {
    if (!config || typeof config !== 'object') {
        return null;
    }
    const value = config[key];

    return isNonEmptyString(value) ? value.trim() : null;
}

function readConversionId(config) {
    if (!config || typeof config !== 'object') {
        return null;
    }
    const value = config.conversion_id;

    return Number.isInteger(value) && value > 0 ? value : null;
}

function claimVendor(contextKey, vendor) {
    const vendorKey = `${contextKey}:${vendor}`;
    if (reportedVendorKeys.has(vendorKey)) {
        return false;
    }
    reportedVendorKeys.add(vendorKey);

    return true;
}

function buildPurchaseParameters(purchase) {
    if (!purchase || typeof purchase !== 'object') {
        return null;
    }
    const { amount, currency } = purchase;
    if (typeof amount !== 'number' || !Number.isFinite(amount)) {
        return null;
    }
    if (typeof currency !== 'string' || currency.length === 0) {
        return null;
    }

    return { currency, value: amount };
}

function reportFacebookPixel({
    advertising,
    step,
    contextKey,
    purchase,
}) {
    const pixelId = readTagIdentifier(advertising[VENDOR_FACEBOOK_PIXEL], 'pixel_id');
    if (pixelId === null || !claimVendor(contextKey, VENDOR_FACEBOOK_PIXEL)) {
        return false;
    }

    placeFacebookPixel(pixelId);
    trackFacebookPixelEvent(pixelId, FACEBOOK_PIXEL_PAGE_VIEW_EVENT);

    const stepEvent = FACEBOOK_PIXEL_EVENT_BY_STEP[step] ?? null;
    if (stepEvent === null) {
        return true;
    }
    if (stepEvent === FACEBOOK_PIXEL_PURCHASE_EVENT) {
        trackFacebookPixelEvent(pixelId, stepEvent, buildPurchaseParameters(purchase));

        return true;
    }
    trackFacebookPixelEvent(pixelId, stepEvent);

    return true;
}

function reportGoogleAdwords({ advertising, step, contextKey }) {
    const conversionId = readConversionId(advertising[VENDOR_GOOGLE_ADWORDS]);
    if (conversionId === null || !claimVendor(contextKey, VENDOR_GOOGLE_ADWORDS)) {
        return false;
    }

    const conversionTarget = `AW-${conversionId}`;
    placeGoogleAdwords(conversionTarget);

    const labelKey = GOOGLE_ADWORDS_LABEL_KEY_BY_STEP[step] ?? null;
    const label = labelKey === null ? null : readTagIdentifier(advertising[VENDOR_GOOGLE_ADWORDS], labelKey);
    if (label === null) {
        return true;
    }

    trackGoogleAdwordsConversion(`${conversionTarget}/${label}`);

    return true;
}

function reportGoogleAnalytics({ advertising, contextKey }) {
    const trackingId = readTagIdentifier(advertising[VENDOR_GOOGLE_ANALYTICS], 'tracking_id');
    if (trackingId === null || !claimVendor(contextKey, VENDOR_GOOGLE_ANALYTICS)) {
        return false;
    }

    placeGoogleAnalytics(trackingId);
    sendGoogleAnalyticsPageView(trackingId);

    return true;
}

function reportGoogleTagManager({ advertising, contextKey }) {
    const containerId = readTagIdentifier(advertising[VENDOR_GOOGLE_TAG_MANAGER], 'container_id');
    if (containerId === null || !claimVendor(contextKey, VENDOR_GOOGLE_TAG_MANAGER)) {
        return false;
    }

    placeGoogleTagManager(containerId);

    return true;
}

const TRACKING_BY_VENDOR = {
    [VENDOR_GOOGLE_ANALYTICS]: { category: CATEGORY_STATISTICS, report: reportGoogleAnalytics },
    [VENDOR_GOOGLE_TAG_MANAGER]: { category: CATEGORY_STATISTICS, report: reportGoogleTagManager },
    [VENDOR_FACEBOOK_PIXEL]: { category: CATEGORY_MARKETING, report: reportFacebookPixel },
    [VENDOR_GOOGLE_ADWORDS]: { category: CATEGORY_MARKETING, report: reportGoogleAdwords },
};

function releaseCheckoutFunnelClaims() {
    const releasablePrefixes = CHECKOUT_FUNNEL_TRACKING_STEPS.map(buildOrganizerTrackingContextKeyPrefix);
    Array.from(reportedVendorKeys)
        .filter(vendorKey => releasablePrefixes.some(prefix => vendorKey.startsWith(prefix)))
        .forEach(vendorKey => reportedVendorKeys.delete(vendorKey));
}

function readWithdrawnCategories() {
    return Array.from(consentedCategoriesInUse)
        .filter(category => !hasCookieCategoryConsent(category));
}

function buildReloadKey(reason, subject) {
    return [
        window.location.href,
        Array.from(placedEventKeys).sort().join(','),
        reason,
        subject,
    ].join('|');
}

function buildResidencyReloadKey(eventKey) {
    return buildReloadKey(RELOAD_REASON_RESIDENCY, eventKey);
}

function buildConsentReloadKey() {
    return buildReloadKey(RELOAD_REASON_CONSENT, Array.from(consentedCategoriesInUse).sort().join(','));
}

function hasAlreadyReloadedForDisagreement(reloadKey) {
    return withSessionStorage(storage => storage.getItem(RELOAD_MARKER)) === reloadKey;
}

function rememberReloadForDisagreement(reloadKey) {
    return withSessionStorage((storage) => {
        storage.setItem(RELOAD_MARKER, reloadKey);

        return true;
    }) === true;
}

function suspendTracking() {
    consentedCategoriesInUse.clear();
    categoriesTheClientIsFeeding.clear();
    reportedVendorKeys.clear();
    placedEventKeys.clear();
    currentContext = null;
    trackingSuspended = true;
}

function claimReloadBudget(reloadKey) {
    return !hasAlreadyReloadedForDisagreement(reloadKey) && rememberReloadForDisagreement(reloadKey);
}

function reloadAfterResidencyDisagreement(eventKey) {
    const reloadKey = buildResidencyReloadKey(eventKey);
    suspendTracking();

    if (claimReloadBudget(reloadKey)) {
        window.location.reload();
    }
}

function reloadAfterConsentWithdrawal(withdrawnCategories) {
    const reloadKey = buildConsentReloadKey();
    const serverAloneKeepsDeclaringThem = tagsCameFromTheMarkup
        && withdrawnCategories.every(category => !categoriesTheClientIsFeeding.has(category));
    suspendTracking();

    if (!serverAloneKeepsDeclaringThem || claimReloadBudget(reloadKey)) {
        window.location.reload();
    }
}

function canonicaliseEventKey(eventKey) {
    const spellings = getServerDeclaredEventKeySpellings();

    return spellings.includes(eventKey) ? spellings[0] : eventKey;
}

function rememberPlacement(category) {
    consentedCategoriesInUse.add(category);
    categoriesTheClientIsFeeding.add(category);

    if (currentContext.eventKey !== null) {
        placedEventKeys.add(canonicaliseEventKey(currentContext.eventKey));
    }
}

function reportCurrentContext() {
    if (trackingSuspended) {
        return;
    }

    const withdrawnCategories = readWithdrawnCategories();
    if (withdrawnCategories.length > 0) {
        reloadAfterConsentWithdrawal(withdrawnCategories);

        return;
    }

    if (currentContext === null) {
        return;
    }

    Object.values(TRACKING_BY_VENDOR).forEach(({ category, report }) => {
        if (hasCookieCategoryConsent(category) && report(currentContext)) {
            rememberPlacement(category);
        }
    });

    if (currentContext.step === ORGANIZER_TRACKING_STEPS.PURCHASE) {
        releaseCheckoutFunnelClaims();
    }
}

function ensureConsentSubscription() {
    if (consentSubscription === null) {
        consentSubscription = subscribeToCookieConsentChange(reportCurrentContext);
    }
}

function enforceOrganizerTrackingResidency(eventKey = null) {
    if (trackingSuspended) {
        return true;
    }

    if (placedEventKeys.size === 0 || placedEventKeys.has(canonicaliseEventKey(eventKey))) {
        return false;
    }

    reloadAfterResidencyDisagreement(eventKey);

    return true;
}

function reportOrganizerTracking({
    advertising,
    step,
    eventKey = null,
    contextKey = null,
    purchase = null,
}) {
    if (enforceOrganizerTrackingResidency(eventKey)) {
        return;
    }

    const taggedConfig = readAdvertisingConfig(advertising);
    if (taggedConfig === null) {
        return;
    }

    currentContext = {
        advertising: taggedConfig,
        step,
        eventKey,
        contextKey: contextKey ?? step,
        purchase,
    };

    ensureConsentSubscription();
    reportCurrentContext();
}

function clearOrganizerTrackingContext() {
    currentContext = null;

    if (consentSubscription !== null && consentedCategoriesInUse.size === 0) {
        consentSubscription();
        consentSubscription = null;
    }
}

function adoptServerPlacement() {
    getServerPlacedTagKeys()
        .map(readOrganizerTagVendor)
        .filter(vendor => vendor !== null)
        .forEach(vendor => consentedCategoriesInUse.add(TRACKING_BY_VENDOR[vendor].category));

    if (consentedCategoriesInUse.size === 0) {
        return;
    }

    tagsCameFromTheMarkup = true;
    getServerPlacedEventKeys().forEach(eventKey => placedEventKeys.add(canonicaliseEventKey(eventKey)));
    ensureConsentSubscription();
}

adoptServerPlacement();

export default reportOrganizerTracking;
export { clearOrganizerTrackingContext, enforceOrganizerTrackingResidency };
